"use client";

import { RiArrowUpLine } from "@remixicon/react";
import { type ComponentProps, useCallback, useEffect, useState } from "react";
import { cn } from "../../lib/utils";

// Wrapper component to handle React 19 type compatibility
function DefaultIcon({ className }: { className?: string }) {
	return <RiArrowUpLine className={className} />;
}

interface ScrollToTopProps extends ComponentProps<"button"> {
	/**
	 * Scroll position threshold to show the button
	 * @default 400
	 */
	showAfter?: number;
	/**
	 * Custom icon component
	 */
	icon?: React.ComponentType<{ className?: string }>;
	/**
	 * Whether to use smooth scrolling
	 * @default true
	 */
	smooth?: boolean;
}

export function ScrollToTop({
	showAfter = 400,
	icon: Icon = DefaultIcon,
	smooth = true,
	className,
	...props
}: ScrollToTopProps) {
	const [isVisible, setIsVisible] = useState(false);

	const toggleVisibility = useCallback(() => {
		if (window.scrollY > showAfter) {
			setIsVisible(true);
		} else {
			setIsVisible(false);
		}
	}, [showAfter]);

	const scrollToTop = useCallback(() => {
		window.scrollTo({
			top: 0,
			behavior: smooth ? "smooth" : "auto",
		});
	}, [smooth]);

	useEffect(() => {
		window.addEventListener("scroll", toggleVisibility);
		toggleVisibility(); // Check initial state

		return () => {
			window.removeEventListener("scroll", toggleVisibility);
		};
	}, [toggleVisibility]);

	if (!isVisible) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={scrollToTop}
			className={cn(
				"fixed right-4 bottom-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl active:scale-95",
				className,
			)}
			aria-label="Scroll to top"
			{...props}
		>
			<Icon className="h-5 w-5" />
		</button>
	);
}
